<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import atendimentoAPI from 'dashboard/api/atendimento';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationLink from '../analytics/ConversationLink.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { QUEM_CORES } from './quemCores';

// As conversas de uma linha do caminho, de 25 em 25, pedidas só quando a linha é
// aberta — com o mesmo recorte da página. A que começou respondendo a um disparo
// leva a marca, com o rótulo e a cor do disparo.
const props = defineProps({
  filters: { type: Object, default: () => ({}) },
  // O filtro "Origem" do Caminho (client | outbound), ou nada.
  origin: { type: String, default: null },
  timezone: { type: String, default: undefined },
});

const { t } = useI18n();
const { number, dateTime } = useAnalyticsFormat();

const dialogRef = ref(null);
const line = ref('');
const label = ref('');
const page = ref(1);
const result = ref(null);
const loading = ref(false);
const failed = ref(false);
let lastRequest = 0;

const load = async () => {
  lastRequest += 1;
  const request = lastRequest;
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await atendimentoAPI.conversas(
      props.filters,
      line.value,
      page.value,
      { origin: props.origin || undefined }
    );
    if (request !== lastRequest) return;
    result.value = data;
  } catch {
    if (request !== lastRequest) return;
    result.value = null;
    failed.value = true;
  } finally {
    if (request === lastRequest) loading.value = false;
  }
};

const open = (key, lineLabel) => {
  line.value = key;
  label.value = lineLabel;
  page.value = 1;
  result.value = null;
  dialogRef.value?.open();
  load();
};

const goTo = next => {
  page.value = next;
  load();
};

const total = computed(() => result.value?.total || 0);
const pages = computed(() =>
  Math.max(Math.ceil(total.value / (result.value?.per_page || 25)), 1)
);
const conversations = computed(() => result.value?.conversations || []);

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      t(
        'ATENDIMENTO_REPORTS.PATH.LIST_TITLE',
        { line: label, count: number(total) },
        total
      )
    "
    :show-confirm-button="false"
    :cancel-button-label="t('ATENDIMENTO_REPORTS.PATH.CLOSE')"
  >
    <div
      v-if="loading && !result"
      class="flex gap-2 justify-center items-center py-8 text-sm text-n-slate-11"
    >
      <Spinner :size="16" />
    </div>
    <p v-else-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.PATH.LIST_FAILED') }}
    </p>
    <p v-else-if="!conversations.length" class="mb-0 text-sm text-n-slate-11">
      {{ t('ATENDIMENTO_REPORTS.PATH.LIST_EMPTY') }}
    </p>
    <template v-else>
      <p
        v-if="result.truncated"
        data-test-id="atendimento-caminho-lista-cortada"
        class="px-3 py-2 mb-0 text-xs rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        {{ t('ATENDIMENTO_REPORTS.PATH.LIST_TRUNCATED') }}
      </p>
      <ul
        data-test-id="atendimento-caminho-lista"
        class="flex flex-col list-none divide-y divide-n-weak"
        :class="{ 'opacity-60': loading }"
      >
        <li
          v-for="item in conversations"
          :key="item.conversation_id"
          class="flex flex-wrap gap-x-3 gap-y-0.5 justify-between items-baseline py-2 min-w-0 text-sm"
        >
          <span class="flex flex-col min-w-0">
            <span class="truncate text-n-slate-12">
              {{ item.contact || t('ATENDIMENTO_REPORTS.PATH.NO_CONTACT') }}
            </span>
            <span class="text-xs truncate text-n-slate-11">
              {{
                item.last_ai_message_at
                  ? t('ATENDIMENTO_REPORTS.PATH.LIST_DETAIL', {
                      inbox: item.inbox,
                      time: dateTime(item.last_ai_message_at, timezone),
                    })
                  : item.inbox
              }}
            </span>
            <span
              v-if="item.from_outbound"
              data-test-id="atendimento-caminho-lista-disparo"
              class="flex gap-1.5 items-center text-xs text-n-slate-11"
            >
              <span
                class="flex-shrink-0 rounded-full size-1.5"
                :class="QUEM_CORES.disparo.bg"
              />
              {{ t('ATENDIMENTO_REPORTS.PATH.OUTBOUND_MARK') }}
            </span>
          </span>
          <ConversationLink :conversation-id="item.conversation_id" />
        </li>
      </ul>
      <div
        v-if="pages > 1"
        class="flex gap-2 justify-between items-center text-xs text-n-slate-11"
      >
        <Button
          :label="t('ATENDIMENTO_REPORTS.PATH.PREVIOUS')"
          xs
          slate
          faded
          type="button"
          :disabled="page <= 1 || loading"
          @click="goTo(page - 1)"
        />
        <span>
          {{ t('ATENDIMENTO_REPORTS.PATH.PAGE', { page, pages }) }}
        </span>
        <Button
          :label="t('ATENDIMENTO_REPORTS.PATH.NEXT')"
          xs
          slate
          faded
          type="button"
          :disabled="page >= pages || loading"
          @click="goTo(page + 1)"
        />
      </div>
    </template>
  </Dialog>
</template>
